import { DATE_FILTER } from 'shared/enum';
import { serialize } from 'shared/helpers';
import { API_GATEWAY_URL } from 'shared/consts/consts';

import BaseService from 'services/baseService';
import UserEmail from 'pages/Messages/MessageOverview/components/MessagesPreview/Email/UserEmail';

const retentionApiEndpoint = new BaseService('/api/v1/retention/');
const responseAnalyticsEndpoint = new BaseService('/api/v1/faq-insights/');
const userEngagementApiEndpoint = new BaseService('/api/v1/insights/');
const genericProxyApiEndpoint = new BaseService('/v2/genericProxyRequest/');
const conversationEngagement7DayApiEndpoint = new BaseService('/api/v1/engagement/TimeRange7Days');
const conversationEngagement30DayApiEndpoint = new BaseService('/api/v1/engagement/TimeRange30Days');
const apiGatewayRequest = new BaseService(API_GATEWAY_URL);

const uploadApi = new BaseService('/v2/importExternalData/', {
	headers: {
		'Content-Type': 'multipart/form-data',
	},
});

export const AnalyticsApi = {
	uploadImportHistory(file, params) {
		const formData = new FormData();
		formData.append('file', file);

		return uploadApi.postRequest(formData, serialize(params, '?'));
	},

	loadUserRetentionMetrics: () => retentionApiEndpoint.getRequest(),

	loadResponseAnalyticsMetrics: (params) => responseAnalyticsEndpoint.getRequest(serialize(params, '?')),

	loadUserEngagementMetrics: (params) => userEngagementApiEndpoint.getRequest(serialize(params, '?')),

	loadConversationEngagementMetrics: (noOfDays) => {
		if (noOfDays === DATE_FILTER.LAST_30_DAYS) {
			return conversationEngagement30DayApiEndpoint.getRequest();
		}

		return conversationEngagement7DayApiEndpoint.getRequest();
	},

	fetchConfiguredChannels: () =>
		genericProxyApiEndpoint.getRequest(
			serialize(
				{
					targetApi: 'getConfiguredChannels',
				},
				'?'
			)
		),

	loadChannelAnalyticsMetrics: ({ channels, fromDate, toDate }) => {
		let requestParams = `?fromDate=${fromDate}&toDate=${toDate}&targetApi=getChannelDashboard`;
		for (const channel of channels) {
			requestParams += `&channelNames=${channel}`;
		}

		return genericProxyApiEndpoint.getRequest(requestParams);
	},

	loadAgentAnalyticsMetrics: ({ fromDate, toDate, page, size }) => {
		const params = {
			page,
			size,
			toDate,
			fromDate,
			moduleName: 'ENGATI_CONNECT',
			targetApi: 'getAgentDashboardTable',
		};

		return genericProxyApiEndpoint.getRequest(serialize(params, '?'));
	},

	loadLiveChatSummaryMetrics: ({ fromDate, toDate }) => {
		const params = {
			toDate,
			fromDate,
			targetApi: 'getLiveChatSummaryDashboard',
		};

		return apiGatewayRequest.getRequest(serialize(params, '?'));
	},

	loadLiveChatConversationOverview: (payload) => genericProxyApiEndpoint.getRequest(serialize(payload, '?')),
	loadAgentPerformanceMetrics: ({ fromDate, toDate, categoryIds, channelNames, page, size }) => {
		const params = {
			page,
			size,
			toDate,
			fromDate,
			moduleName: 'ENGATI_CONNECT',
			targetApi: 'getAgentDashboardData',
			channelNames: channelNames || [],
			categoryIds: categoryIds || [],
		};

		return genericProxyApiEndpoint.getRequestWithParams(params);
	},

	loadTotalChatsHandledData: () => {
		const params = {
			targetApi: 'getTotalChatsHandled',
		};

		return genericProxyApiEndpoint.getRequestWithParams(params);
	},

	loadAgentReplies: (data) => {
		const params = {
			targetApi: 'getAgentReplies',
			...data,
		};

		return genericProxyApiEndpoint.getRequestWithParams(params);
	},

	loadAverageResponseTime: (data) => {
		const params = {
			targetApi: 'getAverageResponseTime',
			...data,
		};

		return genericProxyApiEndpoint.getRequestWithParams(params);
	},

	loadTeamQualityApi: (data) => {
		const params = {
			...data,
			targetApi: 'getTeamQualityData',
		};

		return genericProxyApiEndpoint.getRequestWithParams(params);
	},

	loadAgentQualityApi: (data) => {
		const params = {
			...data,
			targetApi: 'getAgentQualityData',
		};

		return genericProxyApiEndpoint.getRequestWithParams(params);
	},
	loadTeamProductivityApi: (data) => {
		const params = {
			...data,
			targetApi: 'getTeamProductivityData',
		};

		return genericProxyApiEndpoint.getRequestWithParams(params);
	},

	loadAgentProductivityApi: (data) => {
		const params = {
			...data,
			targetApi: 'getAgentProductivityData',
		};

		return genericProxyApiEndpoint.getRequestWithParams(params);
	},
	loadTeamPerformanceApi: (data) => {
		const params = {
			...data,
			targetApi: 'getTeamPerformanceData',
		};

		return genericProxyApiEndpoint.getRequestWithParams(params);
	},

	loadAgentPerformanceApi: (data) => {
		const params = {
			...data,
			targetApi: 'getAgentPerformanceData',
		};

		return genericProxyApiEndpoint.getRequestWithParams(params);
	},

	downloadAgentProductivityData: (data, body) => {
		const params = {
			...data,
			targetApi: 'exportAgentProductivityData',
		};

		return genericProxyApiEndpoint.postRequestWithParams(body, params);
	},
	loadAgentProductivityMetrics: ({ fromDate, toDate, categoryIds, userIds, page, size, meetTypes }) => {
		const params = {
			page,
			size,
			endTime: toDate,
			startTime: fromDate,
			targetApi: 'getAgentProductivityDashboardData',
			userIds: userIds || [],
			categoryIds: categoryIds || [],
			meetTypes: meetTypes || [],
		};

		return genericProxyApiEndpoint.getRequestWithParams(params);
	},
	loadAgentDetailsMetrics: ({ fromDate, toDate, meetTypes, userId, page, size }) => {
		const params = {
			page,
			size,
			endTime: toDate,
			startTime: fromDate,
			meetTypes,
			targetApi: 'getAgentDetailsData',
			userId,
		};

		return genericProxyApiEndpoint.getRequestWithParams(params);
	},
	loadAgentNamesData: () =>
		genericProxyApiEndpoint.getRequest(
			serialize(
				{
					targetApi: 'getAgentNamesData',
				},
				'?'
			)
		),

	exportConversationReportHistory(data) {
		const params = {
			targetApi: 'downloadConversationReportHistory',
		};

		return genericProxyApiEndpoint.postRequest(data, serialize(params, '?'));
	},

	getOverAllFeedback(data) {
		const params = {
			targetApi: 'getAgentAssistanceFeedBackForBotRef',
			...data,
		};

		return genericProxyApiEndpoint.getRequest(serialize(params, '?'));
	},

	getAgentWiseFeedbackData(data) {
		const params = {
			targetApi: 'getAgentAssistanceFeedBackForAgent',
			...data,
		};

		return genericProxyApiEndpoint.getRequest(serialize(params, '?'));
	},

	getDashboards: ({ customerId,userEmail }) => {
		const params = {
			customerId,
			userEmail,
			targetApi: 'accessibleDashboards',
		};

		return apiGatewayRequest.getRequest(serialize(params, '?'));
	},

	getDashboardUrl : ({ customerId, uid, dashboardId }) => {
		const params = {
			customerId,
			uid,
			dashboardId,
			targetApi: 'accessibleDashboardUrl',
		};

		return apiGatewayRequest.getRequest(serialize(params, '?'));
	}

};
